<template>
    <div class="line-chart-template">
        <canvas :id="'chart-'+id" :class="(if_chart_has_data ? '':'hide')" class="line-chart chart-template"></canvas>
        <div :class="(!if_chart_has_data && !if_refreshing ? 'd-inline-block':'hide')" class='text-center'>
            <h1 class="text-secondary mb-1"><i class="fas fa-chart-simple"></i></h1>
            <h4 class="text-secondary mb-2"><em>Press the <strong>Preview</strong> button to render the chart</em></h4>
        </div>
    </div>
</template>

<script>
import { defineComponent, markRaw } from 'vue';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import chartTooltip from '../../../../mixins/chart_tooltip';
import chartBase from '../../../../mixins/chart_base';

export default defineComponent({
    name: 'LineChartTemplate',
    mixins: [chartTooltip, chartBase],
    props: {
        chart_selected: {
            type: Object,
            required: true
        },
        id: {
            type: [String, Number],
            required: true
        },
        if_refreshing: {
            type: Boolean,
            default: false
        },
        if_on_report_page: {
            type: Boolean,
            default: false
        },
        if_benchmark_report: {
            type: Boolean,
            default: false
        },
        applied_chart_filter: {
            type: Object,
            default: () => ({})
        },
        is_cumulative: {
            type: Boolean,
            default: false
        },
        if_show_label: {
            type: Boolean,
            default: false
        },
        size: {
            type: String,
            default: 'small'
        },
        section_id: {
            type: [String, Number],
            default: null
        },
        show_compressed_numbers: {
            type: Boolean,
            default: true
        },
        forceShowAllData: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            chart: null,
            format_type: 'number',
            color: 'yellow',
            num_of_datasets: 0,
            render_timeout: null,
            if_chart_has_data: false,
            if_chart_filter_applied: false,
            chart_filter_label: [],
            chart_filter_label_index: [null, null],
            font_size: 12,
            max_value: 0,
            hoveredIndex: null,
            hoverOpacity: 0,
            hoverAnimationFrame: null,
            hoverTimeout: null,
            hideTimeout: null,
            pendingHoverIndex: null,
            // Pagination and label visibility tracking
            currentPageStart: 0,
            displayedDataCount: 0,
            totalDataLength: 0,
            isDataTruncated: false,
            // Preserve legend visibility across re-renders (e.g. pagination); null = use default (last 3 visible)
            datasetVisibility: null,
            /** When true, next chart render (e.g. after resize) uses no animation. Cleared after render. */
            skipAnimationForNextRender: false,
        };
    },
    watch: {
        chart_selected: {
            handler(newVal) {
                if (newVal?.data) {
                    this.debouncedRender();
                }
            },
            deep: true
        },
        if_refreshing: {
            handler() {
                this.debouncedRender();
            }
        },
        applied_chart_filter: {
            handler(newVal) {
                this.if_chart_filter_applied = newVal?.if_chart_filter_applied && this.id === newVal?.section_chart_id;
                // Sync chart_filter_label from applied_chart_filter when filter is applied to this chart
                if (this.if_chart_filter_applied && newVal?.chart_filters && newVal.chart_filters.length > 0) {
                    this.chart_filter_label = newVal.chart_filters.map(filter => filter.value);
                } else if (!this.if_chart_filter_applied) {
                    this.chart_filter_label = [];
                }
                this.debouncedRender();
            },
            deep: true
        },
        'chart_selected.size': {
            handler(newSize, oldSize) {
                // When chart size changes, reset pagination to first page
                if (newSize !== oldSize) {
                    this.currentPageStart = 0;
                    this.debouncedRender();
                }
            }
        },
        size: {
            handler(newSize, oldSize) {
                if (newSize !== oldSize) {
                    this.skipAnimationForNextRender = true;
                    this.currentPageStart = 0;
                    this.debouncedRender();
                }
            }
        },
    },
    watch: {
        // No additional watchers needed - all handled by chart_base mixin
    },
    methods: {
        /**
         * Override emitPaginationState to emit pagination text
         */
        emitPaginationState() {
            this.$emit('update:pagination', {
                isTruncated: this.isDataTruncated && this.if_chart_has_data,
                currentPageStart: this.currentPageStart,
                displayedDataCount: this.displayedDataCount,
                totalDataLength: this.totalDataLength,
                paginationText: this.getPaginationText()
            });
        },
        
        /**
         * Number of datasets currently visible (1–5). Uses chart legend state when chart exists,
         * otherwise defaults to last 3 years (same as hidden: num_of_datasets - index > 3 in getChartData).
         */
        getVisibleDatasetCount() {
            if (this.chart?.data?.datasets && typeof this.chart.isDatasetVisible === 'function') {
                let count = 0;
                for (let i = 0; i < this.chart.data.datasets.length; i++) {
                    if (this.chart.isDatasetVisible(i)) count++;
                }
                return count || 1;
            }
            return Math.min(3, this.num_of_datasets || 1);
        },
        /**
         * Calculate how many labels (categories) to show based on chart size.
         */
        calculateLabelsToShow() {
            const chartSize = this.size || this.chart_selected?.size || 'small';
            const totalLabels = this.chart_selected.labels?.length || 0;
            const visibleDatasets = this.getVisibleDatasetCount();
            
            if (chartSize === 'small') {
                return Math.min(2, totalLabels);
            }
            if (chartSize === 'medium') {
                return Math.min(5, totalLabels);
            }
            if (chartSize === 'large' || chartSize === 'x-large') {
                // x-large uses same chart width as large (col-4 definitions on the right)
                if (totalLabels < 15) {
                    return totalLabels;
                }
                const labelsToShow = Math.max(1, Math.floor(15 / visibleDatasets));
                return Math.min(labelsToShow, totalLabels);
            }
            return totalLabels;
        },
        renderChart() {
            if (!this.chart_selected?.data) return;

            // Set pagination and dataset state early for accurate calculations
            this.num_of_datasets = Object.keys(this.chart_selected.data).length;
            // Reset stored legend state when chart has different number of datasets (e.g. different chart)
            if (this.datasetVisibility && this.datasetVisibility.length !== this.num_of_datasets) {
                this.datasetVisibility = null;
            }
            this.totalDataLength = this.chart_selected.labels?.length || 0;
            this.displayedDataCount = this.forceShowAllData ? this.totalDataLength : this.calculateLabelsToShow();
            this.isDataTruncated = this.displayedDataCount < this.totalDataLength;

            // Emit state to parent for centralized pagination controls
            this.emitPaginationState();

            // Get full dataset first (will use displayedDataCount for styling)
            const fullDataset = this.getChartData();
            
            // Apply truncation
            const dataset = this.getTruncatedData(fullDataset);
            
            const options = this.getChartOptions(dataset);

            // First destroy any existing chart
            this.destroyChart();

            // Wait for the next tick to ensure DOM is ready and previous chart is fully destroyed
            this.$nextTick(() => {
                const ctx = document.getElementById('chart-' + this.id);
                if (!ctx?.getContext) {
                    console.warn('Chart canvas not found or context not available');
                    return;
                }

                try {
                    this.createChartWhenVisible(ctx, options, (chartInstance) => {
                        this.chart = markRaw(chartInstance);
                        // Setup mouseleave handler (from mixin)
                        this.setupChartMouseLeave(chartInstance);
                        this.skipAnimationForNextRender = false;
                    });
                } catch (error) {
                    // Only log error if it's not a rate limit error
                    if (!error.message?.includes('429')) {
                        console.error('Error creating chart:', error);
                    }
                }
            });
        },
        debouncedRender() {
            if (this.render_timeout) {
                clearTimeout(this.render_timeout);
            }
            this.render_timeout = setTimeout(() => {
                this.$nextTick(() => {
                    this.format_type = this.chart_selected?.metric_formats?.[0] || 'number';
                    this.color = this.chart_selected?.metrics?.[0]?.color || 'yellow';
                    this.num_of_datasets = Object.keys(this.chart_selected.data).length;
                    this.renderChart();
                    this.if_chart_has_data = true;
                    // Re-emit pagination state now that chart has data
                    this.emitPaginationState();
                });
            }, 200); // 200ms debounce
        },
        /**
         * Animates hover opacity from current value to target value
         * @param {number} targetOpacity - Target opacity (0 to 1)
         * @param {Object} chart - Chart.js instance
         */
        /**
         * Whether dataset at index should be hidden. Uses stored legend state when valid, else default (last 3 visible).
         */
        isDatasetHiddenByDefault(index) {
            if (this.datasetVisibility && this.datasetVisibility.length === this.num_of_datasets && this.datasetVisibility[index] !== undefined) {
                return !this.datasetVisibility[index];
            }
            return (this.num_of_datasets - index > 3);
        },
        /**
         * Calculates a nice rounded-up maximum value for the y-axis
         * Logic: if (top_axis - highest_point) < 35% of the space between axis ticks, add additional top axis
         */
        getChartData() {
            var datasetArray = [];
            var element_on_scorecard = document.querySelector('.scorecard-page');
            var max = 0;

            // Get color indices based on number of datasets
            const colorIndices = this.getColorIndicesForDatasets(this.num_of_datasets);
            const colorArray = this.getColor(this.color);
            // Map indices to actual color values
            const chartColors = colorIndices.map(idx => colorArray[idx]);

            if (this.num_of_datasets > 1) {
                // multiple dataset
                var chart_filter_label_index = this.chart_selected.labels.findIndex(label => label == this.chart_filter_label[0]);
                Object.keys(this.chart_selected.data).forEach((key, index) => {
                    var max_temp = Math.max(...this.chart_selected.data[key]);
                    if (max_temp > max) {
                        max = max_temp;
                    }
                    // Get color for this dataset (reverse order: last dataset gets first color)
                    const datasetColorIndex = chartColors.length - 1 - index;
                    const datasetColor = chartColors[datasetColorIndex] || chartColors[0];
                    
                    if (this.if_chart_filter_applied) {
                        // Check if this is a two-dimensional filter (month AND dataset)
                        const isTwoDimensionalFilter = this.chart_filter_label.length > 1;
                        const isMatchingDataset = isTwoDimensionalFilter ? key === this.chart_filter_label[1] : true;
                        
                        var backgroundColorArray = Array(this.chart_selected.data[key].length).fill(this.getColorByLetter('gray', 'D'));
                        
                        // Only highlight the point if this dataset matches the filter (or if it's single dimension filter)
                        if (isMatchingDataset && chart_filter_label_index >= 0) {
                            backgroundColorArray[chart_filter_label_index] = chartColors[0]; // Use color A
                        }
                        
                        // Map index to gray color (reverse: index 0 -> G, index 1 -> F, etc.)
                        const grayIndex = Math.max(0, Math.min(6 - index, 6));
                        const grayLetter = ['G', 'F', 'E', 'D', 'C', 'B', 'A'][grayIndex] || 'A';
                        
                        // Always use gray for the line border - only the dot should be highlighted
                        const borderColor = this.getColorByLetter('gray', grayLetter);
                        
                        const basePointRadius = (this.chart_selected.data[key].length > 60) ? 0 : 4;
                        datasetArray.push({
                            label: key==""? '(not set)':key,
                            data: this.chart_selected.data[key],
                            pointRadius: basePointRadius,
                            pointHoverRadius: basePointRadius, // Disable default hover animation
                            pointBorderColor: 'white',
                            pointBorderWidth: 1,
                            backgroundColor: backgroundColorArray,
                            borderColor: borderColor,
                            borderWidth: this.if_benchmark_report ? 3 : 1.5,
                            borderDash: this.if_benchmark_report ? [10,5] : [],
                            borderRadius: 3,
                            type: 'line',
                            order: this.num_of_datasets - index + 1,
                            hidden: this.isDatasetHiddenByDefault(index),
                            isMetricColorDataset: isMatchingDataset,
                            metricColorPointIndex: isMatchingDataset ? chart_filter_label_index : -1 // Store which point has metric color
                        });
                    } else if(this.chart_selected.highlight_last_value) {
                        const isLastDataset = index == this.num_of_datasets - 1;
                        let borderColor, backgroundColor;
                        if (isLastDataset) {
                            backgroundColor = chartColors[0]; // Use color A
                            borderColor = chartColors[0];
                        } else {
                            // Map index to gray color (reverse: index 0 -> G, index 1 -> F, etc.)
                            const grayIndex = Math.max(0, Math.min(6 - index - 1, 6));
                            const grayLetter = ['G', 'F', 'E', 'D', 'C', 'B', 'A'][grayIndex] || 'A';
                            backgroundColor = this.getColorByLetter('gray', grayLetter);
                            borderColor = this.getColorByLetter('gray', grayLetter);
                        }
                        const basePointRadius = (this.chart_selected.data[key].length > 60) ? 0 : 4;
                        datasetArray.push({
                            label: key==""? '(not set)':key,
                            data: this.chart_selected.data[key],
                            pointRadius: basePointRadius,
                            pointHoverRadius: basePointRadius, // Disable default hover animation
                            pointBorderColor: 'white',
                            pointBorderWidth: 1,
                            backgroundColor: backgroundColor,
                            borderColor: borderColor,
                            borderWidth: this.if_benchmark_report ? 3 : 1.5,
                            borderDash: this.if_benchmark_report && (index!= this.num_of_datasets-1) ? [10,5] : [],
                            borderRadius: 3,
                            type: 'line',
                            order: this.num_of_datasets - index + 1,
                            hidden: this.isDatasetHiddenByDefault(index),
                            isMetricColorDataset: isLastDataset // Store flag to identify metric color dataset
                        });
                    } else {
                        const isLastDataset = index == this.num_of_datasets - 1;
                        const basePointRadius = (this.chart_selected.data[key].length > 60) ? 0 : 4;
                        datasetArray.push({
                            label: key==""? '(not set)':key,
                            data: this.chart_selected.data[key],
                            pointRadius: basePointRadius,
                            pointHoverRadius: basePointRadius, // Disable default hover animation
                            pointBorderColor: 'white',
                            pointBorderWidth: 1,
                            backgroundColor: datasetColor,
                            borderColor: datasetColor,
                            borderWidth: this.if_benchmark_report ? 3 : 1.5,
                            borderDash: this.if_benchmark_report && (index!= this.num_of_datasets-1) ? [10,5] : [],
                            borderRadius: 3,
                            type: 'line',
                            order: this.num_of_datasets - index + 1,
                            hidden: this.isDatasetHiddenByDefault(index),
                            isMetricColorDataset: isLastDataset
                        });
                    }
                });
            } else {
                // single dataset - use color A
                const chartColor = this.getColorByLetter(this.color, 'A');
                var key = Object.keys(this.chart_selected.data)[0];
                var backgroundColorArray = [];
                var max_temp = Math.max(...this.chart_selected.data[key]);
                if (max_temp > max) {
                    max = max_temp;
                }

                if (element_on_scorecard && this.chart_selected.data[key].length >= 3) {
                    backgroundColorArray = Array(this.chart_selected.data[key].length).fill(chartColor);
                    for(var i = backgroundColorArray.length - 1; i >= backgroundColorArray.length - 3; i--) {
                        backgroundColorArray[i] = 'rgba(' + chartColor.substring(4, chartColor.length - 1) + ', 0.5)';
                    }
                } else {
                    if (this.if_benchmark_report) { // benchmark with only 1 dataset means the client's own data is not shown, disregard the highlight_last_value config
                        backgroundColorArray = Array(this.chart_selected.data[key].length).fill(chartColor);
                    } else if (this.if_chart_filter_applied) {
                        var index = this.chart_selected.labels.findIndex(label => label == this.chart_filter_label[0]);
                        backgroundColorArray = Array(this.chart_selected.data[key].length).fill(this.getColorByLetter('gray', 'D'));
                        backgroundColorArray[index] = chartColor;
                    } else {
                        backgroundColorArray = Array(this.chart_selected.data[key].length).fill(chartColor);
                    }
                }

                const basePointRadius = (this.chart_selected.data[key].length > 60) ? 0 : 4;
                datasetArray.push({
                    label: key==""? '(not set)':key,
                    data: this.chart_selected.data[key],
                    pointRadius: basePointRadius,
                    pointHoverRadius: basePointRadius, // Disable default hover animation
                    pointBorderColor: 'white',
                    pointBorderWidth: 1,
                    backgroundColor: backgroundColorArray,
                    borderColor: backgroundColorArray,
                    borderWidth: this.if_benchmark_report ? 3 : 1.5,
                    borderDash: this.if_benchmark_report ? [10,5] : [],
                    borderRadius: 3,
                    type: 'line',
                    order: 1,
                    isMetricColorDataset: true
                });
            }

            this.max_value = max;
            return {
                labels: this.chart_selected.labels,
                datasets: datasetArray
            };
        },
        getChartOptions(data) {
            var self = this;

            // Custom plugin to draw background color for hovered category
            const hoverBackgroundPlugin = {
                id: 'hoverBackground',
                beforeDatasetsDraw: (chart) => {
                    if (self.hoveredIndex !== null && chart.scales.x) {
                        const ctx = chart.ctx;
                        const xScale = chart.scales.x;
                        const yScale = chart.scales.y;
                        
                        // Get shade E of the metric color with 30% opacity
                        const colorE = self.getColorByLetter(self.color, 'E');
                        const rgbMatch = colorE.match(/rgb\((\d+)\s+(\d+)\s+(\d+)\)/);
                        
                        if (rgbMatch) {
                            const r = rgbMatch[1];
                            const g = rgbMatch[2];
                            const b = rgbMatch[3];
                            const rgbaColor = `rgba(${r}, ${g}, ${b}, ${0.3 * self.hoverOpacity})`;
                            
                            // Get the pixel positions for the hovered category and adjacent categories
                            const hoveredPixelValue = xScale.getPixelForValue(self.hoveredIndex);
                            const totalWidth = xScale.width;
                            const numCategories = xScale.ticks.length;
                            const categorySpacing = totalWidth / numCategories;
                            
                            // Calculate left edge: midpoint between previous category and hovered category
                            let leftEdge;
                            if (self.hoveredIndex > 0) {
                                const prevPixelValue = xScale.getPixelForValue(self.hoveredIndex - 1);
                                leftEdge = (prevPixelValue + hoveredPixelValue) / 2;
                            } else {
                                // For first category, use midpoint between start and current
                                leftEdge = hoveredPixelValue - categorySpacing / 2;
                            }
                            
                            // Calculate right edge: midpoint between hovered category and next category
                            let rightEdge;
                            if (self.hoveredIndex < numCategories - 1) {
                                const nextPixelValue = xScale.getPixelForValue(self.hoveredIndex + 1);
                                rightEdge = (hoveredPixelValue + nextPixelValue) / 2;
                            } else {
                                // For last category, use midpoint between current and end
                                rightEdge = hoveredPixelValue + categorySpacing / 2;
                            }
                            
                            // Clamp to chart area so overlay never covers y-axis or x-axis labels
                            const chartLeft = chart.chartArea.left;
                            const chartRight = chart.chartArea.right;
                            const leftEdgeClipped = Math.max(leftEdge, chartLeft);
                            const rightEdgeClipped = Math.min(rightEdge, chartRight);
                            
                            // Calculate x position and width for the wider highlight box
                            const x = leftEdgeClipped;
                            const categoryWidth = rightEdgeClipped - leftEdgeClipped;
                            const y = yScale.top;
                            
                            // Extend height to include axis labels
                            // Get the bottom of the x-axis scale which includes the label area
                            const xAxisBottom = xScale.bottom || chart.chartArea.bottom;
                            const height = xAxisBottom - yScale.top;
                            
                            // Calculate radius - ensure it doesn't exceed half the height or width
                            const maxRadius = Math.min(categoryWidth / 2, height / 2, 30);
                            const radius = Math.max(5, maxRadius); // Minimum 5px radius
                            
                            // Draw rounded rectangle
                            ctx.save();
                            ctx.fillStyle = rgbaColor;
                            ctx.beginPath();
                            // Top-left corner
                            ctx.moveTo(x + radius, y);
                            // Top edge
                            ctx.lineTo(x + categoryWidth - radius, y);
                            // Top-right corner
                            ctx.quadraticCurveTo(x + categoryWidth, y, x + categoryWidth, y + radius);
                            // Right edge
                            ctx.lineTo(x + categoryWidth, y + height - radius);
                            // Bottom-right corner
                            ctx.quadraticCurveTo(x + categoryWidth, y + height, x + categoryWidth - radius, y + height);
                            // Bottom edge
                            ctx.lineTo(x + radius, y + height);
                            // Bottom-left corner
                            ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
                            // Left edge
                            ctx.lineTo(x, y + radius);
                            // Top-left corner
                            ctx.quadraticCurveTo(x, y, x + radius, y);
                            ctx.closePath();
                            ctx.fill();
                            ctx.restore();
                        }
                    }
                }
            };

            // Custom plugin to draw dots on top of all lines and make metric color dots bigger on hover
            const enlargeMetricColorPointPlugin = {
                id: 'enlargeMetricColorPoint',
                beforeDatasetsDraw: (chart) => {
                    // Hide original points to draw them on top later in afterDatasetsDraw
                    chart.data.datasets.forEach((dataset) => {
                        if (dataset.data.length > 60) return;
                        
                        // Store original radius if not already stored
                        if (dataset._originalPointRadius === undefined) {
                            dataset._originalPointRadius = dataset.pointRadius;
                        }
                        // Temporarily set pointRadius to 0 to hide default points
                        dataset.pointRadius = 0;
                    });
                },
                afterDatasetsDraw: (chart) => {
                    const ctx = chart.ctx;
                    
                    chart.data.datasets.forEach((dataset, datasetIndex) => {
                        const meta = chart.getDatasetMeta(datasetIndex);
                        // Only process visible datasets
                        if (!meta || meta.hidden || !chart.isDatasetVisible(datasetIndex)) return;
                        
                        // Skip if data length > 60 (points are hidden)
                        if (dataset.data.length > 60) return;
                        
                        const isMetricColorDataset = dataset.isMetricColorDataset === true;
                        const metricColorPointIndex = dataset.metricColorPointIndex;
                        
                        // Use stored original radius or default
                        const baseRadius = typeof dataset._originalPointRadius === 'number' 
                            ? dataset._originalPointRadius 
                            : 4;

                        meta.data.forEach((point, dataIndex) => {
                            if (!point) return;
                            
                            // Get point properties
                            const { x, y } = point.getProps(['x', 'y']);
                            
                            // Get the point color
                            let pointColor;
                            if (Array.isArray(dataset.backgroundColor)) {
                                pointColor = dataset.backgroundColor[dataIndex];
                            } else {
                                pointColor = dataset.backgroundColor;
                            }
                            
                            // Check if this point is currently hovered
                            const isHoveredIndex = self.hoveredIndex === dataIndex;
                            
                            // Determine if this point should be enlarged (metric color dataset or specific point)
                            let shouldEnlarge = false;
                            if (isMetricColorDataset) {
                                shouldEnlarge = true;
                            } else if (metricColorPointIndex !== undefined && metricColorPointIndex === dataIndex) {
                                shouldEnlarge = true;
                            }
                            
                            // Determine point radius and border width
                            let radius = baseRadius;
                            let borderWidth = 1;
                            
                            if (isHoveredIndex) {
                                // Enlarged state during hover
                                radius = shouldEnlarge ? 6 : 4;
                                borderWidth = 2;
                            }

                            if (radius <= 0) return;
                            
                            // Draw the point with white border
                            ctx.save();
                            
                            // Draw fill
                            ctx.fillStyle = pointColor;
                            ctx.beginPath();
                            ctx.arc(x, y, radius, 0, Math.PI * 2);
                            ctx.fill();
                            
                            // Draw white border
                            ctx.strokeStyle = 'white';
                            ctx.lineWidth = borderWidth;
                            ctx.beginPath();
                            ctx.arc(x, y, radius, 0, Math.PI * 2);
                            ctx.stroke();
                            
                            ctx.restore();
                        });
                    });
                }
            };

            // Custom plugin to draw solid line at y=0 (base x-axis) extending across chart area including x-axis labels
            const solidBaseLinePlugin = {
                id: 'solidBaseLine',
                beforeDatasetsDraw: (chart) => {
                    const ctx = chart.ctx;
                    const yScale = chart.scales.y;
                    const xScale = chart.scales.x;
                    
                    // Get the y position for zero
                    const zeroY = yScale.getPixelForValue(0);
                    
                    // Use chart area boundaries and extend slightly to cover x-axis labels
                    // Start from the left edge of the chart area (where bars start)
                    // End at the right edge of the chart area, extending slightly to cover labels
                    const leftEdge = chart.chartArea.left;
                    const rightEdge = xScale.right; // Extend to cover x-axis labels
                    
                    // Draw solid line extending across the chart area and x-axis labels
                    ctx.save();
                    ctx.strokeStyle = 'rgba(206, 219, 215, 1)';
                    ctx.lineWidth = 1;
                    ctx.setLineDash([]); // Solid line - empty array means no dashes
                    ctx.beginPath();
                    ctx.moveTo(leftEdge, zeroY);
                    ctx.lineTo(rightEdge, zeroY);
                    ctx.stroke();
                    ctx.restore();
                }
            };

            var config = {
                plugins: [solidBaseLinePlugin, hoverBackgroundPlugin, enlargeMetricColorPointPlugin, ChartDataLabels],
                type: 'line',
                data: data,
                options: {
                    interaction: {
                        intersect: false,
                        mode: 'index',
                    },
                    onHover: (event, activeElements, chart) => {
                        if (activeElements && activeElements.length > 0) {
                            // Clear any pending hide timeout when hovering over an element
                            if (this.hideTimeout) {
                                clearTimeout(this.hideTimeout);
                                this.hideTimeout = null;
                            }
                            
                            const activeElement = activeElements[0];
                            const dataIndex = activeElement.index;
                            
                            // If hovering over a different data point OR returning to a point that's not currently hovered,
                            // (re)start the debounce timer
                            if (dataIndex !== this.pendingHoverIndex || this.hoveredIndex !== dataIndex) {
                                // Cancel any pending show timeout
                                if (this.hoverTimeout) {
                                    clearTimeout(this.hoverTimeout);
                                }
                                
                                // Reset current state immediately when moving to a new point
                                // to keep highlight and tooltip in sync (both hidden during the debounce)
                                if (this.hoveredIndex !== null && this.hoveredIndex !== dataIndex) {
                                    this.hoveredIndex = null;
                                    this.hoverOpacity = 0;
                                    chart.update('none');
                                }
                                
                                // Reset flags for new data point
                                this.pendingHoverIndex = dataIndex;
                                
                                // Start new debounce timer - 200ms delay to show hover
                                this.hoverTimeout = setTimeout(() => {
                                    this.hoverTimeout = null;
                                    // Apply hover effect after delay
                                    if (this.hoveredIndex !== dataIndex) {
                                        this.hoveredIndex = dataIndex;
                                        chart.update('none'); // Immediate update for point size and tooltip
                                        this.animateHoverOpacity(1, chart);
                                    }
                                }, 200);
                            }
                        } else {
                            // Clear any pending show timeout when leaving
                            if (this.hoverTimeout) {
                                clearTimeout(this.hoverTimeout);
                                this.hoverTimeout = null;
                                this.pendingHoverIndex = null;
                            }
                            
                            // Debounce hiding the hover state (200ms delay)
                            // This matches the show debounce timing for consistency
                            if (this.hoveredIndex !== null && !this.hideTimeout) {
                                this.hideTimeout = setTimeout(() => {
                                    // Only hide if still not hovering and tooltip is also not being hovered
                                    if (this.hoveredIndex !== null && !this.isTooltipHovered) {
                                        this.hoveredIndex = null;
                                        chart.update('none'); // Immediate update for point size and tooltip
                                        this.animateHoverOpacity(0, chart);

                                        // Explicitly hide tooltip element to stay in sync
                                        let tooltipEl = document.getElementById('chart-' + this.id + '-' + this.effectiveSectionId + '-tooltip');
                                        if (tooltipEl) {
                                            tooltipEl.style.opacity = 0;
                                            tooltipEl.style.pointerEvents = 'none';
                                        }
                                    }
                                    this.hideTimeout = null;
                                }, 200);
                            }
                        }
                    },
                    animation: {
                        duration: this.skipAnimationForNextRender ? 0 : 500,
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: {
                        padding: {
                            right: 50,
                            left: 10,
                            bottom: 10,
                        }
                    },
                    animations: {
                        x: {
                            duration: 500,
                        }
                    },
                    indexAxis: 'x',
                    scales: {
                        x: {
                            grid: {
                                drawBorder: false,
                                display: false
                            },
                            border: {
                                display: false,
                            },
                            ticks: {
                                font: {
                                    size: this.font_size,
                                    weight: 400,
                                    family: "'Nunito', sans-serif",
                                },
                                maxRotation: 90,
                                autoSkip: true,
                                callback: function(value, index, ticks, context) {
                                    // Get the label text from the truncated data (data.labels contains the sliced labels)
                                    let labelText = '';
                                    if(data.labels && data.labels[value]){
                                        labelText = data.labels[value] + "";
                                    } else {
                                        labelText = '(not set)';
                                    }
                                    
                                    // Break label into multiple lines to prevent collisions
                                    // Maximum characters per line (adjust based on font size and chart width)
                                    const maxCharsPerLine = 12;
                                    const words = labelText.split(" ");
                                    const lines = [];
                                    let currentLine = '';
                                    
                                    words.forEach((word, wordIndex) => {
                                        // If adding this word would exceed the limit, start a new line
                                        const testLine = currentLine ? currentLine + ' ' + word : word;
                                        
                                        if (testLine.length > maxCharsPerLine && currentLine) {
                                            // Save current line and start a new one
                                            lines.push(currentLine);
                                            currentLine = word;
                                        } else {
                                            // Add word to current line
                                            currentLine = testLine;
                                        }
                                        
                                        // If this is the last word, add the current line
                                        if (wordIndex === words.length - 1 && currentLine) {
                                            lines.push(currentLine);
                                        }
                                    });
                                    
                                    // Return array of lines (Chart.js will render each as a separate line)
                                    // If only one line, return as single-element array for consistency
                                    return lines.length > 0 ? lines : [labelText];
                                }
                            }
                        },
                        y: {
                            display: true,
                            position: 'left',
                            beginAtZero: true,
                            // Ensure there's always space above the highest point
                            max: this.getYAxisMax(this.max_value),
                            border: {
                                display: false,
                                width: 2,
                                dash: [5,5],
                                dashOffset: 0,
                            },
                            grid: {
                                drawOnChartArea: true,
                                drawBorder: false,
                                color: 'rgba(206, 219, 215, 1)',
                                lineWidth: 1,
                                borderDash: [2,2],
                                drawTicks: true,
                                tickColor: 'rgba(206, 219, 215, 1)',
                                tickLength: 20,
                                tickBorderDash: [2,2],
                                tickBorderDashOffset: 0,
                                z: -2,
                            },
                            ticks: {
                                font: {
                                    size: this.font_size,
                                    weight: 400,
                                    family: "'Nunito', sans-serif",
                                },
                                offset: false,
                                autoSkip: true,
                                maxTicksLimit: 5,
                                callback: function(value, index, ticks, context) {
                                    if(self.format_type == 'money') {
                                        if(self.show_compressed_numbers && value >= 1000) {
                                            return self.currency_abbr(value);
                                        }
                                        return self.currency_with_zero(value);
                                    }
                                    if(self.format_type == 'number') {
                                        if(self.show_compressed_numbers && value >= 1000) {
                                            return self.num_abbr(value);
                                        }
                                        if(self.max_value >= 10) {
                                            return self.number_with_zero(value);
                                        }
                                        return self.number_with_decimal(value);
                                    }
                                    if(self.format_type == 'percent')
                                        return self.percentage(value);
                                }
                            }
                        }
                    },
                    plugins: {
                        legend: {
                            position: (data.datasets.length > 1) ? 'bottom' : 'none',
                            display: true,
                            align: 'center',
                            boxHeight: 40,
                            fullSize: true,
                            onClick: (e, legendItem, legend) => {
                                const chart = legend.chart;
                                const datasetIndex = legendItem.datasetIndex;

                                chart.setDatasetVisibility(datasetIndex, !chart.isDatasetVisible(datasetIndex));
                                chart.update();
                                // Preserve visibility so pagination/re-render keeps legend state
                                const self = this;
                                self.datasetVisibility = chart.data.datasets.map((_, i) => chart.isDatasetVisible(i));
                            },
                            onHover: function(e, chart) {
                                e.native.target.style.cursor = 'pointer';
                            },
                            onLeave: function(e, chart) {
                                e.native.target.style.cursor = 'default';
                            },
                            labels: {
                                usePointStyle: true,
                                pointStyle: 'line',
                                pointStyleWidth: 20,
                                lineWidth: 2,
                                padding: 30,
                                generateLabels: (chart) => {
                                    return chart.data.datasets.map((dataset, index) => {
                                        // Use borderColor (line color) for the legend line
                                        // If borderColor is an array, use the first color
                                        const lineColor = Array.isArray(dataset.borderColor) 
                                            ? dataset.borderColor[0] 
                                            : dataset.borderColor;

                                        return {
                                            text: dataset.label,
                                            datasetIndex: index,
                                            hidden: !chart.isDatasetVisible(index),
                                            pointStyle: 'line',
                                            fillStyle: lineColor,
                                            strokeStyle: lineColor,
                                            lineWidth: 2,
                                        };
                                    }); 
                                },
                                font: {
                                    size: this.font_size,
                                    weight: 400,
                                    family: "'Nunito', sans-serif",
                                },
                            },
                        },
                        tooltip: {
                            enabled: false,
                            external: function(context) {
                                // Verify that the chart ID matches before showing tooltip
                                const chartCanvasId = context.chart?.canvas?.id;
                                if (!chartCanvasId || !chartCanvasId.startsWith('chart-')) {
                                    return;
                                }

                                // Extract chart ID from canvas ID (format: 'chart-{id}')
                                const chartIdFromCanvas = chartCanvasId.replace(/^chart-/, '');
                                const expectedTooltipId = self.getTooltipId();

                                // Require that the chart that triggered this callback matches this component
                                if (chartIdFromCanvas !== String(self.id)) {
                                    let tooltipEl = document.getElementById(expectedTooltipId);
                                    if (tooltipEl) {
                                        tooltipEl.style.opacity = 0;
                                        tooltipEl.style.pointerEvents = 'none';
                                    }
                                    return;
                                }

                                // Get or create tooltip element using mixin method
                                let tooltipEl = self.getOrCreateTooltipElement();
                                if (!tooltipEl) return;

                                // Require tooltip element ID to match the chart that triggered the callback
                                const tooltipIdPrefix = 'chart-' + chartIdFromCanvas + '-';
                                if (!tooltipEl.id.startsWith(tooltipIdPrefix) || !tooltipEl.id.endsWith('-tooltip')) {
                                    tooltipEl.style.opacity = 0;
                                    tooltipEl.style.pointerEvents = 'none';
                                    return;
                                }
                                if (tooltipEl.id !== expectedTooltipId) {
                                    tooltipEl.style.opacity = 0;
                                    tooltipEl.style.pointerEvents = 'none';
                                    return;
                                }

                                const tooltipModel = context.tooltip;

                                // Hide logic: only hide if no tooltip is active AND user is not hovering the element
                                if (!tooltipModel || tooltipModel.opacity === 0) {
                                    if (!self.isTooltipHovered && self.hoveredIndex === null && tooltipEl) {
                                        tooltipEl.style.opacity = 0;
                                        tooltipEl.style.pointerEvents = 'none';
                                        self.hideAllChartTooltips();
                                    }
                                    return;
                                }
                                
                                // Get the current data index being hovered
                                const currentDataIndex = tooltipModel.dataPoints && tooltipModel.dataPoints.length > 0 
                                    ? tooltipModel.dataPoints[0].dataIndex 
                                    : null;
                                
                                // Only show if this index matches the synchronized hoveredIndex
                                if (currentDataIndex === null || currentDataIndex !== self.hoveredIndex) {
                                    tooltipEl.style.opacity = 0;
                                    tooltipEl.style.pointerEvents = 'none';
                                    // Hide all other chart tooltips so a different chart's tooltip doesn't stay visible
                                    self.hideAllChartTooltips();
                                    return;
                                }

                                // Determine which group is hovered for smart positioning
                                const dataIndex = tooltipModel.dataPoints && tooltipModel.dataPoints.length > 0 
                                    ? tooltipModel.dataPoints[0].dataIndex 
                                    : null;

                                // Hide tooltips from other sections using mixin method
                                self.hideOtherTooltips(tooltipEl);

                                // Apply base styles using mixin method
                                self.applyTooltipBaseStyles(tooltipEl);

                                // Set Text
                                if (tooltipModel.body) {
                                    const titleLines = tooltipModel.title || [];
                                    const bodyLines = tooltipModel.body.map(b => b.lines);

                                    let innerHtml = '<thead>';
                                    innerHtml += '<tr><th style="font-weight: 500; font-size: 18px; padding-bottom: 4px;">' + titleLines[0] + '</th></tr>';
                                    innerHtml += '</thead><tbody>';

                                    let tooltipBody = '';

                                    bodyLines.forEach((body, i) => {
                                        if (!body || !body[0]) return;

                                        const body_label_temp = body[0].split(':');
                                        const body_label = body_label_temp.slice(0, body_label_temp.length - 1).join(':');
                                        const value = tooltipModel.dataPoints[i]?.raw;

                                        if (value === undefined) return;

                                        const colors = tooltipModel.labelColors[i];
                                        
                                        // Determine text color based on filter state
                                        let textColor = colors.backgroundColor;
                                        
                                        // If filter is applied and it's a two-dimensional filter, only highlight matching dataset
                                        if (self.if_chart_filter_applied && self.chart_filter_label.length > 1) {
                                            const isMatchingDataset = body_label === self.chart_filter_label[1];
                                            textColor = isMatchingDataset ? colors.backgroundColor : self.getColorByLetter('gray', 'D');
                                        }

                                        let formattedValue = '';
                                        if (self.num_of_datasets > 1) {
                                            if(self.format_type === 'money' && self.max_value >= 10) {
                                                formattedValue = self.currency_with_zero(value);
                                            } else if(self.format_type === 'money' && self.max_value < 10) {
                                                formattedValue = self.currency_with_decimal(value);
                                            } else if(self.format_type === 'number' && self.max_value >= 10) {
                                                formattedValue = self.number_with_zero(value);
                                            } else if(self.format_type === 'number' && self.max_value < 10) {
                                                formattedValue = self.number_with_decimal(value);
                                            } else if(self.format_type === 'percent') {
                                                formattedValue = self.percentage(value);
                                            }
                                            const individualFilterButton = !self.if_benchmark_report
                                                ? '<button id="'+ i +'" class="btn btn-sm btn-white tooltip-btn-white btn-explore-individual p-0 ms-2" style="width:24px !important; height: 24px !important; border-radius: 50% !important; display: flex !important; align-items: center !important; justify-content: center !important;"><img class="icon" src="/img/icons/avid-icons/filter-funnel-01.svg" style="width: 14px; height: 14px; margin: 0 !important;" alt=""></button>'
                                                : '';
                                            tooltipBody += '<tr><td class="d-flex align-items-center pe-4" style="padding-top: 1px; padding-bottom: 1px;"><span style="color: ' + textColor + '; font-weight: 400;">' + body_label + '</span>: <span style="color: #111921; font-weight: 400; margin-left: 4px;">' + formattedValue + '</span>' + individualFilterButton + '</td></tr>';
                                        } else {
                                            if(self.format_type === 'money' && self.max_value >= 10) {
                                                formattedValue = self.currency_with_zero(value);
                                            } else if(self.format_type === 'money' && self.max_value < 10) {
                                                formattedValue = self.currency_with_decimal(value);
                                            } else if(self.format_type === 'number' && self.max_value >= 10) {
                                                formattedValue = self.number_with_zero(value);
                                            } else if(self.format_type === 'number' && self.max_value < 10) {
                                                formattedValue = self.number_with_decimal(value);
                                            } else if(self.format_type === 'percent') {
                                                formattedValue = self.percentage(value);
                                            }
                                            tooltipBody += '<tr><td style="padding-top: 1px; padding-bottom: 1px;"><span class="tooltip-single-dataset-value" style="color: #111921; font-weight: 400; font-size: 32px;">' + formattedValue + '</span></td></tr>';
                                        }
                                    });

                                    innerHtml += tooltipBody;

                                    if(self.if_on_report_page && !self.if_benchmark_report) {
                                        const isFilterApplied = self.if_chart_filter_applied && self.chart_filter_label[0] === titleLines[0];

                                        innerHtml += '<div class="d-flex justify-content-end my-2">' +
                                            '<button class="btn btn-sm btn-primary d-flex align-items-center px-4" style="font-weight: 500;">' +
                                            '<img class="icon me-1" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="">' + (isFilterApplied ? 'Remove as Filter' : 'Filter') +
                                            '</button>' +
                                            '</div>';

                                        innerHtml += '</tbody>';

                                        let tableRoot = tooltipEl.querySelector('table');
                                        if (tableRoot) {
                                            tableRoot.innerHTML = innerHtml;

                                            // Handle individual explore buttons (only for multiple datasets)
                                            if (self.num_of_datasets > 1) {
                                                const btnExploreIndividuals = tooltipEl.querySelectorAll('.btn-explore-individual');
                                                if (self.if_chart_filter_applied) {
                                                    // Disable individual buttons when filter is applied
                                                    btnExploreIndividuals.forEach((btn) => {
                                                        btn.disabled = true;
                                                    });
                                                } else {
                                                    // Show and enable individual buttons when no filter is applied
                                                    btnExploreIndividuals.forEach((btn) => {
                                                        btn.hidden = false;
                                                        btn.disabled = false;
                                                        btn.style.pointerEvents = 'auto';
                                                        btn.style.opacity = '1';
                                                        
                                                        // Disable if chart filter is applied on another chart
                                                        const isFilterOnOtherChart = self.applied_chart_filter?.if_chart_filter_applied && 
                                                                                     !self.if_chart_filter_applied;
                                                        btn.disabled = isFilterOnOtherChart;
                                                        
                                                        if (!btn.disabled) {
                                                            btn.addEventListener('click', (event) => {
                                                                event.preventDefault();
                                                                event.stopPropagation();
                                                                
                                                                self.if_chart_filter_applied = true;
                                                                self.chart_filter_label = [];

                                                                const filter_array = [];
                                                                const datasetIndex = parseInt(event.currentTarget.id);
                                                                
                                                                // Build filters for both dimensions (x-axis category and dataset)
                                                                self.chart_selected?.group_by?.forEach((dimension, index) => {
                                                                    let value_temp = null;
                                                                    
                                                                    if (index === 0) {
                                                                        // First dimension: use the x-axis category (title)
                                                                        value_temp = titleLines[0];
                                                                    } else if (index === 1) {
                                                                        // Second dimension: use the dataset label from the clicked row
                                                                        const bodyLine = bodyLines[datasetIndex]?.[0];
                                                                        if (bodyLine) {
                                                                            const body_label_temp = bodyLine.split(':');
                                                                            value_temp = body_label_temp.slice(0, body_label_temp.length - 1).join(':').trim();
                                                                        }
                                                                    }
                                                                    
                                                                    if (value_temp) {
                                                                        self.chart_filter_label.push(value_temp);
                                                                        filter_array.push({
                                                                            name: dimension.text,
                                                                            column: dimension.value,
                                                                            value: value_temp
                                                                        });
                                                                    }
                                                                });

                                                                // Only emit if we have at least one filter
                                                                if (filter_array.length > 0) {
                                                                    self.$emit('exploreChart', {
                                                                        chart_selected: self.chart_selected,
                                                                        chart_filters: filter_array
                                                                    });
                                                                }
                                                            });
                                                        }
                                                    });
                                                }
                                            }

                                            // Find the main Filter button
                                            let btn = tooltipEl.querySelector('.btn-primary');
                                            if (btn) {
                                                // Disable button if chart filter is applied on another chart
                                                const isFilterOnOtherChart = self.applied_chart_filter?.if_chart_filter_applied && 
                                                                             !self.if_chart_filter_applied;
                                                
                                                // Disable button if filter is applied on this chart and hovering over a different point
                                                const isFilterOnThisChart = self.if_chart_filter_applied;
                                                let isDifferentSelection = false;
                                                
                                                if (isFilterOnThisChart) {
                                                    const hoveredPoint = titleLines[0];
                                                    const filteredPoint = self.chart_filter_label[0];
                                                    
                                                    if (hoveredPoint !== filteredPoint) {
                                                        isDifferentSelection = true;
                                                    }
                                                }
                                                
                                                btn.disabled = isFilterOnOtherChart || (isFilterOnThisChart && isDifferentSelection);

                                                if (self.if_chart_filter_applied) {
                                                    btn.addEventListener('click', () => {
                                                        self.if_chart_filter_applied = false;
                                                        self.chart_filter_label = [];
                                                        self.$emit('exploreChart', {
                                                            chart_selected: null,
                                                            chart_filters: []
                                                        });
                                                    });
                                                } else {
                                                    btn.addEventListener('click', () => {
                                                        self.if_chart_filter_applied = true;
                                                        self.chart_filter_label = [titleLines[0]];
                                                        self.$emit('exploreChart', {
                                                            chart_selected: self.chart_selected,
                                                            chart_filters: [{
                                                                name: self.chart_selected.group_by[0].text,
                                                                column: self.chart_selected.group_by[0].value,
                                                                value: titleLines[0]
                                                            }]
                                                        });
                                                    });
                                                }
                                            }
                                        }
                                    } else {
                                        innerHtml += '</tbody>';
                                        let tableRoot = tooltipEl.querySelector('table');
                                        if (tableRoot) {
                                            tableRoot.innerHTML = innerHtml;
                                        }
                                    }
                                }
                                
                                // Position tooltip using mixin method
                                self.positionTooltipOnSide(tooltipEl, context, dataIndex);
                            }
                        },
                        datalabels: {
                            anchor: 'center',
                            align: function(context) {
                                const index = context.datasetIndex;
                                // Alternate between positions to avoid collision (Top-Right, Bottom-Left, Bottom-Right, Top-Left)
                                // Using more vertical angles and smaller offset to stay close to dots
                                const positions = [-30, 150, 30, 240];
                                return positions[index % 4];
                            },
                            offset: 4,
                            color: function(context) {
                                const baseColor = self.chartDataLabelColor;
                                
                                // If labels are always shown (toggle ON), return full opacity
                                if (self.if_show_label) {
                                    const dataset = context.dataset || {};
                                    
                                    // Check if this point should be highlighted due to filter
                                    if (self.if_chart_filter_applied && self.chart_filter_label.length > 0) {
                                        const isTwoDimensionalFilter = self.chart_filter_label.length > 1;
                                        const datasetLabel = dataset.label === '(not set)' ? '' : dataset.label;
                                        const isMatchingDataset = isTwoDimensionalFilter ? datasetLabel === self.chart_filter_label[1] : true;
                                        const chart_filter_index = self.chart_selected.labels.findIndex(label => label == self.chart_filter_label[0]);
                                        
                                        // If this is the filtered point, use metric color
                                        if (isMatchingDataset && context.dataIndex === chart_filter_index) {
                                            return self.getColorByLetter(self.color, 'A');
                                        }
                                    }
                                    
                                    // Default color logic
                                    const datasetColor = dataset.borderColor ?? dataset.backgroundColor;
                                    if (Array.isArray(datasetColor)) {
                                        return datasetColor[context.dataIndex] ?? datasetColor[0] ?? self.getColorByLetter(self.color, 'A');
                                    }
                                    return datasetColor ?? self.getColorByLetter(self.color, 'A');
                                }
                                
                                // For hover labels when toggle is OFF, apply animated opacity
                                if (self.hoveredIndex !== null && context.dataIndex === self.hoveredIndex) {
                                    return `rgba(17, 25, 33, ${self.hoverOpacity})`;
                                }
                                
                                return baseColor;
                            },
                            display: function(context) {
                                // When toggle is ON: show labels only for visible datasets (respect legend-hidden state)
                                if (self.if_show_label) {
                                    const meta = context.chart.getDatasetMeta(context.datasetIndex);
                                    if (meta && meta.hidden) return false;
                                    return true;
                                }
                                
                                // Show labels for hovered group when toggle is OFF
                                if (self.hoveredIndex !== null && context.dataIndex === self.hoveredIndex) {
                                    return true;
                                }
                                
                                // Use shouldShowDataLabels() to determine base visibility
                                if (!self.shouldShowDataLabels()) {
                                    return false;
                                }
                                
                                // Additional logic for time-based charts with > 15 data points
                                // When shouldShowDataLabels() returns true but we have > 15 points,
                                // show labels only for the most recent year
                                const chartSize = self.size || self.chart_selected?.size || 'small';
                                const totalLabels = self.chart_selected.labels?.length || 0;
                                
                                if ((chartSize === 'large' || chartSize === 'x-large') && self.isTimeBased() && totalLabels > 15) {
                                    const mostRecentIndices = self.getMostRecentPeriodIndices();
                                    // Adjust indices based on currentPageStart for truncated data (though large/x-large shouldn't truncate)
                                    const adjustedIndices = mostRecentIndices.map(idx => idx - self.currentPageStart);
                                    return adjustedIndices.includes(context.dataIndex);
                                }
                                
                                // For all other cases where shouldShowDataLabels() returns true, show the label
                                return true;
                            },
                            font: {
                                size: this.font_size,
                            },
                            formatter: function(value, context) {
                                // When toggle is ON
                                if (self.if_show_label) {
                                    // For time-based charts, only format for the colored line
                                    if (self.isTimeBased()) {
                                        const datasetIndex = context.datasetIndex;
                                        const dataset = context.chart.data.datasets[datasetIndex];
                                        
                                        if (!dataset) {
                                            return null;
                                        }
                                        
                                        // Check if this is the metric color dataset (colored line)
                                        const isMetricColorDataset = dataset.isMetricColorDataset === true;
                                        
                                        // Only format if this is the colored line (marked as metric color)
                                        if (!isMetricColorDataset) {
                                            return null;
                                        }
                                    }
                                    
                                    // Format the value
                                    return self.formatDataLabelValue(
                                        value, 
                                        self.format_type, 
                                        context.datasetIndex,
                                        false
                                    );
                                }
                                
                                // Map the label back to the original key for data access
                                var dataKey = context.dataset.label === '(not set)' ? '' : context.dataset.label;
                                var dataArray = self.chart_selected.data[dataKey];
                                var shouldShowAllDataLabels = self.chart_selected.show_all_data_labels;
                                if (!dataArray) {
                                    return null;
                                }
                                
                                // When hovering, show labels for all datasets at the hovered index
                                if (self.hoveredIndex !== null && context.dataIndex === self.hoveredIndex) {
                                    return self.formatDataLabelValue(
                                        value, 
                                        self.format_type, 
                                        context.datasetIndex,
                                        false
                                    );
                                }
                                
                                // Handle filter state first (takes priority over default visibility rules)
                                if (self.if_chart_filter_applied && !shouldShowAllDataLabels) {
                                    var chart_filter_index = self.chart_selected.labels.findIndex((label) => label == self.chart_filter_label[0]);
                                    
                                    // Check if this is a two-dimensional filter (month AND dataset)
                                    const isTwoDimensionalFilter = self.chart_filter_label.length > 1;
                                    
                                    // For two-dimensional filters, only show label if both dataset and index match
                                    if (isTwoDimensionalFilter) {
                                        const datasetLabel = context.dataset.label === '(not set)' ? '' : context.dataset.label;
                                        const isMatchingDataset = datasetLabel === self.chart_filter_label[1];
                                        
                                        if (!isMatchingDataset || context.dataIndex !== chart_filter_index) {
                                            return null;
                                        }
                                        // If we reach here, this is the filtered point - show its label
                                    } else {
                                        // For single-dimensional filters, just check the index
                                        if(context.dataIndex != chart_filter_index)
                                            return null;
                                    }
                                } else {
                                    // Original logic when not hovering and no filter applied
                                    if (!shouldShowAllDataLabels) {
                                        if(dataArray.length > 30 && (!self.is_cumulative || context.dataIndex != dataArray.length - 1))
                                            return null;
                                        if(context.datasetIndex < context.chart.data.datasets.length-1)
                                            return null;
                                    }
                                }

                                return self.formatDataLabelValue(
                                    value, 
                                    self.format_type, 
                                    context.datasetIndex,
                                    false
                                );
                            },
                        }
                    },
                    title: {
                        display: false,
                    },
                }
            };
            return config;
        }
    }
});
</script>
